import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, buildQuery } from '../../lib/api';
import { EmptyState, ErrorState, LoadingState } from '../../components/Feedback';
import { formatDateTime } from '../../lib/utils';
import type { AuditEntry } from '../../types';

const ACTION_LABELS: Record<string, string> = {
    'application.created': 'Application créée',
    'application.updated': 'Application modifiée',
    'application.archived': 'Application archivée',
    'access.granted': 'Accès accordé',
    'access.revoked': 'Accès révoqué',
    'access.updated': 'Accès modifié',
    'request.created': 'Demande déposée',
    'request.approved': 'Demande approuvée',
    'request.rejected': 'Demande refusée',
    'request.cancelled': 'Demande annulée',
    'user.role_changed': 'Rôle modifié',
    'user.deactivated': 'Compte désactivé',
    'user.reactivated': 'Compte réactivé',
    'entra.synced': 'Synchronisation Entra ID',
    'announcement.created': 'Actualité ajoutée',
    'announcement.archived': 'Actualité retirée',
    'news_feed.created': 'Flux RSS ajouté',
    'news_feed.updated': 'Flux RSS modifié',
    'news_feed.deleted': 'Flux RSS retiré',
};

const ENTITY_LABELS: Record<string, string> = {
    application: 'Application',
    instance: 'Instance',
    access: 'Droit',
    request: 'Demande',
    user: 'Utilisateur',
    system: 'Système',
};

export function AdminAuditPage() {
    const [entityType, setEntityType] = useState('');

    const { data: entries = [], isLoading, isError, error, refetch } = useQuery({
        queryKey: ['admin-audit', entityType],
        queryFn: () =>
            api.get<AuditEntry[]>(`/api/admin/audit${buildQuery({ entityType, limit: '200' })}`),
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <select
                    value={entityType}
                    onChange={(event) => setEntityType(event.target.value)}
                    className="select w-auto"
                    aria-label="Filtrer par type d’objet"
                >
                    <option value="">Tous les objets</option>
                    {Object.entries(ENTITY_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>

                <p className="text-sm text-[var(--color-text-secondary)]">
                    Toute modification de droit laisse une trace ici.
                </p>
            </div>

            {isLoading ? (
                <LoadingState />
            ) : isError ? (
                <ErrorState
                    message={error instanceof Error ? error.message : 'Chargement impossible.'}
                    onRetry={() => void refetch()}
                />
            ) : entries.length === 0 ? (
                <EmptyState title="Journal vide" description="Aucune opération enregistrée." />
            ) : (
                <div className="card overflow-x-auto">
                    <table className="table">
                        <thead>
                            <tr>
                                <th scope="col">Date</th>
                                <th scope="col">Auteur</th>
                                <th scope="col">Action</th>
                                <th scope="col">Détail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry) => (
                                <tr key={entry.id}>
                                    <td className="text-[var(--color-text-secondary)] whitespace-nowrap text-xs">
                                        {formatDateTime(entry.createdAt)}
                                    </td>
                                    <td className="text-[var(--color-text-secondary)] text-xs">
                                        {entry.actorEmail ?? 'Système'}
                                    </td>
                                    <td>
                                        <span className="badge badge-neutral">
                                            {ACTION_LABELS[entry.action] ?? entry.action}
                                        </span>
                                    </td>
                                    <td className="text-xs text-[var(--color-text-secondary)]">
                                        {formatPayload(entry)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/** Rend le détail d'une entrée sous forme lisible, sans exposer de JSON brut. */
function formatPayload(entry: AuditEntry): string {
    const payload = entry.payload;
    if (!payload) return ENTITY_LABELS[entry.entityType] ?? entry.entityType;

    const parts: string[] = [];

    if (typeof payload.applicationName === 'string') parts.push(payload.applicationName);
    if (typeof payload.userEmail === 'string') parts.push(payload.userEmail);
    if (typeof payload.targetEmail === 'string') parts.push(payload.targetEmail);
    if (typeof payload.roleName === 'string') parts.push(`niveau ${payload.roleName}`);
    if (typeof payload.role === 'string') parts.push(`rôle ${payload.role}`);
    if (typeof payload.name === 'string' && parts.length === 0) parts.push(payload.name);
    if (typeof payload.title === 'string' && parts.length === 0) parts.push(payload.title);
    if (typeof payload.reason === 'string') parts.push(`motif : ${payload.reason}`);
    if (typeof payload.note === 'string') parts.push(`note : ${payload.note}`);

    return parts.length > 0 ? parts.join(' · ') : (ENTITY_LABELS[entry.entityType] ?? entry.entityType);
}
